import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { interviewQueries } from '@/db/queries';
import { toast } from 'sonner';

/**
 * Chat message format for UI display
 */
export interface ChatMessage {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
}

/**
 * Hook return type
 */
export interface UseInterviewReturn {
  messages: ChatMessage[];
  sendMessage: (input: string) => Promise<void>;
  isLoading: boolean;
  isInitializing: boolean;
  isComplete: boolean;
  interviewId: string | null;
  error: string | null;
  completionPercentage: number;
  restartInterview: () => Promise<void>;
  reinitialize: () => Promise<void>;
}

/**
 * Simplified interview hook using Lovable AI Gateway
 * Replaces the complex 494-line useSurveyAgent
 */
export function useInterview(): UseInterviewReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  const [interviewId, setInterviewId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [userId, setUserId] = useState<string | null>(null);
  const [phase1Data, setPhase1Data] = useState<any>(null);
  
  // Collected insights from conversation
  const insightsRef = useRef<Record<string, any>>({});
  
  // Conversation history for context
  const conversationRef = useRef<Array<{ role: 'user' | 'assistant'; content: string }>>([]);

  /**
   * Core initialization logic - extracted for reuse
   */
  const initialize = useCallback(async () => {
    try {
      setIsInitializing(true);
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('Please sign in to start your emergency plan');
        setIsInitializing(false);
        return;
      }
      setUserId(user.id);

      // Find Phase 1 completed interview
      const { data: interview, error: fetchError } = await supabase
        .from('interviews')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'in_progress')
        .gte('phase_completed', 1)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (fetchError) {
        console.error('Error fetching interview:', fetchError);
        setError('Failed to load your interview data');
        setIsInitializing(false);
        return;
      }

      if (!interview) {
        // No Phase 1 data - user needs to fill out the form first
        setIsInitializing(false);
        return;
      }

      setInterviewId(interview.id);
      setPhase1Data(interview);

      // Check if we have existing conversation history
      if (interview.conversation_history && Array.isArray(interview.conversation_history) && interview.conversation_history.length > 0) {
        // Resume existing conversation
        const history = interview.conversation_history as Array<{ role: string; content: string; timestamp?: string }>;
        conversationRef.current = history.map(msg => ({
          role: msg.role as 'user' | 'assistant',
          content: msg.content,
        }));
        
        const chatMessages = history.map((msg, idx) => ({
          id: `msg-${idx}`,
          content: msg.content,
          isUser: msg.role === 'user',
          timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
        }));
        setMessages(chatMessages);
        
        // Estimate progress from conversation length
        setCompletionPercentage(Math.min(90, chatMessages.length * 10));
      } else {
        // Generate initial message from AI
        await generateInitialMessage(interview);
      }

    } catch (err) {
      console.error('Initialization error:', err);
      setError('Failed to start interview');
    } finally {
      setIsInitializing(false);
    }
  }, []);

  /**
   * Run initialization on mount
   */
  useEffect(() => {
    initialize();
  }, [initialize]);

  /**
   * Reinitialize hook - call this after form completion to trigger AI greeting
   */
  const reinitialize = useCallback(async () => {
    // Reset state before reinitializing
    setMessages([]);
    setIsComplete(false);
    setCompletionPercentage(0);
    insightsRef.current = {};
    conversationRef.current = [];
    setInterviewId(null);
    setPhase1Data(null);
    
    // Run initialization again
    await initialize();
  }, [initialize]);

  /**
   * Generate personalized opening message
   */
  const generateInitialMessage = async (interview: any) => {
    try {
      setIsLoading(true);

      const response = await supabase.functions.invoke('interview-chat', {
        body: {
          userMessage: null, // No user message yet
          conversationHistory: [],
          phase1Data: {
            name: interview.name,
            location: interview.location,
            emergency_types: interview.emergency_types,
            household_members: interview.household_members,
            property_type: interview.property_type,
            property_ownership: interview.property_ownership,
            emergency_contact: interview.emergency_contact,
          },
          collectedInsights: {},
        },
      });

      if (response.error) {
        throw response.error;
      }

      const aiResponse = response.data;
      const openingMessage: ChatMessage = {
        id: 'msg-0',
        content: aiResponse.display_text,
        isUser: false,
        timestamp: new Date(),
      };

      setMessages([openingMessage]);
      conversationRef.current = [{ role: 'assistant', content: aiResponse.display_text }];
      
      // Store any initial insights
      if (aiResponse.extracted_insights) {
        insightsRef.current = { ...insightsRef.current, ...aiResponse.extracted_insights };
      }
      
      setCompletionPercentage(aiResponse.completion_percentage || 5);

      // Save to database
      await saveConversation(interview.id);

    } catch (err) {
      console.error('Failed to generate initial message:', err);
      // Fallback message
      const fallback: ChatMessage = {
        id: 'msg-0',
        content: `Hi ${interview.name}! I have your basic information from the form. Now I'd like to ask some deeper questions to create a truly personalized emergency plan for you. Ready to continue?`,
        isUser: false,
        timestamp: new Date(),
      };
      setMessages([fallback]);
      conversationRef.current = [{ role: 'assistant', content: fallback.content }];
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Send user message and get AI response
   */
  const sendMessage = useCallback(async (input: string) => {
    if (!input.trim() || isLoading || isComplete || !interviewId) return;

    setError(null);
    setIsLoading(true);

    // Add user message immediately (optimistic update)
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      content: input,
      isUser: true,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);
    conversationRef.current.push({ role: 'user', content: input });

    try {
      const response = await supabase.functions.invoke('interview-chat', {
        body: {
          userMessage: input,
          conversationHistory: conversationRef.current,
          phase1Data: phase1Data ? {
            name: phase1Data.name,
            location: phase1Data.location,
            emergency_types: phase1Data.emergency_types,
            household_members: phase1Data.household_members,
            property_type: phase1Data.property_type,
            property_ownership: phase1Data.property_ownership,
            emergency_contact: phase1Data.emergency_contact,
          } : null,
          collectedInsights: insightsRef.current,
        },
      });

      if (response.error) {
        throw response.error;
      }

      const aiResponse = response.data;

      // Add AI response to messages
      const aiMessage: ChatMessage = {
        id: `ai-${Date.now()}`,
        content: aiResponse.display_text,
        isUser: false,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, aiMessage]);
      conversationRef.current.push({ role: 'assistant', content: aiResponse.display_text });

      // Update insights
      if (aiResponse.extracted_insights) {
        insightsRef.current = { ...insightsRef.current, ...aiResponse.extracted_insights };
      }

      // Update completion percentage
      setCompletionPercentage(aiResponse.completion_percentage || 0);

      // Check if complete
      if (aiResponse.is_complete) {
        setIsComplete(true);
        setCompletionPercentage(100);
        
        // Update interview status
        await interviewQueries.complete(interviewId);
        
        // Save insights to database
        await supabase
          .from('interviews')
          .update({
            ai_insights: insightsRef.current,
            nuanced_details: insightsRef.current,
          })
          .eq('id', interviewId);

        toast.success('Interview complete! Generating your emergency plan...');
      }

      // Save conversation to database
      await saveConversation(interviewId);

    } catch (err: any) {
      console.error('Send message error:', err);
      setError(err.message || 'Failed to send message');
      
      // Add error message
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        content: "I'm having trouble connecting right now. Please try again.",
        isUser: false,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, isComplete, interviewId, phase1Data]);

  /**
   * Save conversation to database
   */
  const saveConversation = async (intId: string) => {
    try {
      const historyWithTimestamps = conversationRef.current.map((msg, idx) => ({
        ...msg,
        timestamp: new Date().toISOString(),
      }));

      await supabase
        .from('interviews')
        .update({
          conversation_history: historyWithTimestamps,
          updated_at: new Date().toISOString(),
        })
        .eq('id', intId);
    } catch (err) {
      console.error('Failed to save conversation:', err);
    }
  };

  /**
   * Restart interview from beginning
   */
  const restartInterview = useCallback(async () => {
    if (!userId) return;

    try {
      setIsInitializing(true);
      setError(null);

      // Mark current as abandoned
      if (interviewId) {
        await supabase
          .from('interviews')
          .update({ status: 'abandoned' })
          .eq('id', interviewId);
      }

      // Reset state
      setMessages([]);
      setIsComplete(false);
      setCompletionPercentage(0);
      insightsRef.current = {};
      conversationRef.current = [];

      // Find Phase 1 interview again and regenerate
      const { data: interview } = await supabase
        .from('interviews')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'in_progress')
        .gte('phase_completed', 1)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (interview) {
        setInterviewId(interview.id);
        setPhase1Data(interview);
        await generateInitialMessage(interview);
        toast.success('Interview restarted');
      }

    } catch (err) {
      console.error('Restart error:', err);
      setError('Failed to restart interview');
    } finally {
      setIsInitializing(false);
    }
  }, [userId, interviewId]);

  return {
    messages,
    sendMessage,
    isLoading,
    isInitializing,
    isComplete,
    interviewId,
    error,
    completionPercentage,
    restartInterview,
    reinitialize,
  };
}
